import {useEffect, useState} from "react";
import {Button, Input, Table, Space, Tabs, Form, Select, Switch} from "antd";
import {useTranslation} from "react-i18next";
import ApiService from "../../core/service/api-service";
import moment from "moment";

export default function PatientSelectModal({onSelected, studyInfo, hideAllStudiesOption = false, canCreatePatient = true}) {
    const {t} = useTranslation();
    const [activeTab, setActiveTab] = useState('select');
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [allStudies, setAllStudies] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [filter, setFilter] = useState({
        dicomPatientId: '',
        name: ''
    });
    
    // New patient form
    const [form] = Form.useForm();
    const [creating, setCreating] = useState(false);
    
    // Auto-fill form when switching to create tab
    const handleTabChange = (key) => {
        setActiveTab(key);
        if (key === 'create' && studyInfo) {
            const birthdateStr = studyInfo.birthdate 
                ? moment(studyInfo.birthdate).format('YYYY-MM-DD') 
                : '';
            form.setFieldsValue({
                dicomPatientId: studyInfo.dicomPatientId || '',
                name: studyInfo.patientName || '',
                birthdate: birthdateStr,
                gender: studyInfo.gender || undefined
            });
        }
    };

    const columns = [
        {
            title: t('Patient ID'),
            dataIndex: 'dicomPatientId',
            key: 'dicomPatientId',
            width: 150,
        },
        {
            title: t('Patient Name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('Birth Date'),
            dataIndex: 'birthdate',
            key: 'birthdate',
            width: 120,
        },
        {
            title: t('Gender'),
            dataIndex: 'gender',
            key: 'gender',
            width: 80,
        },
    ];

    const fetchPatients = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const gridInput = {
                paginationQuery: {
                    pageNumber: page,
                    pageSize: pageSize
                },
                filter: {
                    dicomPatientId: filter.dicomPatientId || null,
                    name: filter.name || null
                },
                orderByItems: []
            };
            
            const response = await ApiService.Patient.DataGrid(gridInput);
            if (response.success) {
                setPatients(response.data);
                setPagination({
                    current: response.pagination?.pageNumber || page,
                    pageSize: response.pagination?.pageSize || pageSize,
                    total: response.pagination?.totalRecords || 0
                });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPatients();
    }, []);

    const handleSearch = () => {
        fetchPatients(1, pagination.pageSize);
    };

    const handleTableChange = (newPagination) => {
        fetchPatients(newPagination.current, newPagination.pageSize);
    };

    const rowSelection = {
        type: 'radio',
        selectedRowKeys: selectedPatient ? [selectedPatient.id] : [],
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedPatient(selectedRows[0] || null);
        },
    };
    
    const handleCreatePatient = async (values) => {
        setCreating(true);
        try {
            // Parse birthdate from string (YYYY-MM-DD format)
            let birthdateISO = null;
            if (values.birthdate) {
                const parsed = moment(values.birthdate, 'YYYY-MM-DD', true);
                if (parsed.isValid()) {
                    birthdateISO = parsed.toISOString();
                }
            }
            
            const data = {
                name: values.name,
                dicomPatientId: values.dicomPatientId,
                birthdate: birthdateISO,
                gender: values.gender || null
            };
            
            const response = await ApiService.Patient.Create(data);
            if (response.success) {
                // Fetch the created patient and select it
                const patientId = response.data;
                const createdPatient = {
                    id: patientId,
                    name: values.name,
                    dicomPatientId: values.dicomPatientId,
                    birthdate: values.birthdate,
                    gender: values.gender
                };
                onSelected && onSelected(createdPatient);
            }
        } finally {
            setCreating(false);
        }
    };

    const tabItems = [
        {
            key: 'select',
            label: t('Select Patient'),
            children: (
                <>
                    <Space style={{marginBottom: 16}} wrap>
                        <Input
                            placeholder={t('Patient ID')}
                            value={filter.dicomPatientId}
                            onChange={(e) => setFilter({...filter, dicomPatientId: e.target.value})}
                            style={{width: 150}}
                            onPressEnter={handleSearch}
                        />
                        <Input
                            placeholder={t('Patient Name')}
                            value={filter.name}
                            onChange={(e) => setFilter({...filter, name: e.target.value})}
                            style={{width: 200}}
                            onPressEnter={handleSearch}
                        />
                        <Button type="primary" onClick={handleSearch}>
                            {t('Search')}
                        </Button>
                    </Space>
                    
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={patients}
                        loading={loading}
                        rowSelection={rowSelection}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showTotal: (total) => `${t('Total')}: ${total}`,
                            pageSizeOptions: ['10', '20', '50', '100']
                        }}
                        onChange={handleTableChange}
                        size="small"
                        scroll={{y: 400}}
                        onRow={(record) => ({
                            onClick: () => {
                                setSelectedPatient(record);
                            },
                            style: {
                                cursor: 'pointer',
                                backgroundColor: selectedPatient?.id === record.id ? '#e6f7ff' : undefined
                            }
                        })}
                    />
                    
                    <div style={{marginTop: 16, textAlign: 'right'}}>
                        {
                            !hideAllStudiesOption && (
                                <Space style={{ marginRight: 16 }}>
                                    <span>{t('All Studies')}</span>
                                    <Switch
                                        checked={allStudies}
                                        onChange={setAllStudies}
                                    />
                                </Space>
                            )
                        }
                        <Button
                            type="primary"
                            disabled={!selectedPatient}
                            onClick={() => {
                                onSelected && onSelected(selectedPatient, allStudies);
                            }}
                        >
                            {t('Select')}
                        </Button>
                    </div>
                </>
            )
        },
        {
            key: 'create',
            label: t('Create New Patient'),
            children: (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreatePatient}
                    style={{maxWidth: 500}}
                >
                    <Form.Item
                        name="dicomPatientId"
                        label={t('Patient ID')}
                        rules={[{required: true, message: t('Please enter patient ID')}]}
                    >
                        <Input placeholder={t('Patient ID')} />
                    </Form.Item>
                    
                    <Form.Item
                        name="name"
                        label={t('Patient Name')}
                        rules={[{required: true, message: t('Please enter patient name')}]}
                    >
                        <Input placeholder={t('Patient Name')} />
                    </Form.Item>
                    
                    <Form.Item
                        name="birthdate"
                        label={t('Birth Date')}
                        extra={t('Format: YYYY-MM-DD (e.g. 1990-05-15)')}
                    >
                        <Input 
                            placeholder="1990-01-31"
                        />
                    </Form.Item>
                    
                    <Form.Item
                        name="gender"
                        label={t('Gender')}
                    >
                        <Select placeholder={t('Select gender')} allowClear>
                            <Select.Option value="M">{t('Male')}</Select.Option>
                            <Select.Option value="F">{t('Female')}</Select.Option>
                            <Select.Option value="O">{t('Other')}</Select.Option>
                        </Select>
                    </Form.Item>
                    
                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={creating}>
                            {t('Create and Select')}
                        </Button>
                    </Form.Item>
                </Form>
            )
        }
    ].filter(tab => tab.key !== 'create' || canCreatePatient);

    return (
        <div style={{minWidth: 600}}>
            <Tabs 
                activeKey={activeTab} 
                onChange={handleTabChange}
                items={tabItems}
            />
        </div>
    );
}
